// src/routes/WorkspaceRoutes.js
import React, { useState, useEffect, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useWorkspace } from "./context/WorkspaceContext";
import { isMobile as checkIsMobile, handleResize, debounce } from './utils/utils';

// Import LoadingIndicator and navigation components
import { LoadingIndicator } from "./components/Tools/LoadingIndicator";
import VerticalNavbar from "./components/Navigation/VerticalNavbar/VerticalNavbar";
import HorizontalMobileNavbar from "./components/Navigation/HorizontalMobileNavbar/HorizontalMobileNavbar";
import MobileHeader from "./components/Navigation/MobileHeader/MobileHeader";
import AdminRoute from "./pages/Protected/Admin/AdminRoute";
import AIGenerator from "./pages/Protected/AI/AIGenerator";
import SubscriptionGate from "./pages/Auth/SubscriptionGate/SubscriptionGate";

// Lazy load pages
const Dashboard = React.lazy(() => import("./pages/Protected/Dashboard/Dashboard"));
const Upload = React.lazy(() => import("./pages/Protected/Upload/Upload"));
const Schedule = React.lazy(() => import("./pages/Protected/Schedule/Schedule"));
const Templates = React.lazy(() => import("./pages/Protected/Tools/Templates/Templates"));
const History = React.lazy(() => import("./pages/Protected/History/History"));
const Socials = React.lazy(() => import("./pages/Protected/Socials/Socials"));
const Settings = React.lazy(() => import("./pages/Protected/Settings/Settings"));
const Support = React.lazy(() => import("./pages/Protected/Support/Support"));
const Library = React.lazy(() => import("./pages/Protected/Library/Library"));
const Analytics = React.lazy(() => import("./pages/Protected/Analytics/Analytics"));
const InstagramGrid = React.lazy(() => import("./pages/Protected/Tools/InstagramGrid/InstagramGrid"));
const Tools = React.lazy(() => import("./pages/Protected/Tools/Tools"));
const EarningsCalculator = React.lazy(() => import("./pages/Public/Tools/EarningsCalculator"));
const LinkInBio = React.lazy(() => import("./pages/Protected/Tools/LinkInBio/LinkInBio"));
const LinkInBioPublic = React.lazy(() => import("./pages/Public/LinkInBioPublic"));
const ReferralDashboard = React.lazy(() => import("./pages/Protected/Referral/ReferralDashboard"));
const ImageEditor = React.lazy(() => import("./pages/Protected/AI/ImageEditor"));

// Callback routes are now handled in App.js to allow access during onboarding

// Admin pages
const Admin = React.lazy(() => import("./pages/Protected/Admin/Admin"));

// Special pages
const NoWorkspace = React.lazy(() => import("./pages/Protected/Settings/components/NoWorkspace/NoWorkspace"));

// Fallback component for lazy loading
const PageLoader = () => <div className="flex justify-center items-center h-screen">
    <LoadingIndicator showLogo={true} />
</div>;

function WorkspaceRoutes() {
    const [isMobile, setIsMobile] = useState(checkIsMobile());

    useEffect(() => {
        const debouncedHandleResize = debounce(() => handleResize(setIsMobile), 100);
        window.addEventListener("resize", debouncedHandleResize);
        return () => {
            window.removeEventListener("resize", debouncedHandleResize);
        };
    }, []);

    const {
        loading,
        workspaces,
    } = useWorkspace();

    if (loading) {
        return <LoadingIndicator showLogo={true} className="h-screen w-screen" />;
    }

    // Render navbars based on userLoggedIn and isMobile
    return (
        <SubscriptionGate>
            <div className={`flex ${isMobile && 'flex-col'} min-h-screen w-full`}>

                {!isMobile && <VerticalNavbar />}

                <div className="flex flex-col flex-1">
                    {isMobile && <MobileHeader />}

                    {/* Existing routing logic */}
                    {workspaces.length === 0 ? (
                        <Suspense fallback={<PageLoader />}>
                            <Routes>
                                <Route path="/dashboard/no-workspace" element={<NoWorkspace />} />
                                <Route path="*" element={<Navigate to="/dashboard/no-workspace" />} />
                            </Routes>
                        </Suspense>
                    ) : (
                        <Suspense fallback={<PageLoader />}>
                            <Routes>
                                <Route path="/" element={<Navigate to="/dashboard" />} />
                                <Route path="/dashboard" element={<Dashboard />} />
                                <Route path="/dashboard/upload" element={<Upload />} />
                                <Route path="/dashboard/library" element={<Library />} />
                                <Route path="/dashboard/schedule" element={<Schedule />} />
                                <Route path="/dashboard/posts" element={<History />} />
                                <Route path="/dashboard/socials" element={<Socials />} />
                                <Route path="/dashboard/analytics" element={<Analytics />} />

                                {/* Settings and nested routes */}
                                <Route path="/dashboard/settings/*" element={<Settings />} />

                                <Route path="/dashboard/support" element={<Support />} />

                                {/* TOOLS */}
                                <Route path="/dashboard/tools" element={<Tools />} />
                                <Route path="/dashboard/tools/templates" element={<Templates />} />
                                <Route path="/dashboard/tools/instagram-grid" element={<InstagramGrid />} />
                                <Route path="/dashboard/tools/earnings-calculator" element={<EarningsCalculator />} />
                                <Route path="/dashboard/tools/link-in-bio" element={<LinkInBio />} />
                                <Route path="/dashboard/tools/ai-generator" element={<AIGenerator />} />
                                <Route path="/dashboard/tools/image-editor" element={<ImageEditor />} />
                                
                                <Route path="/linkinbio/:customUrl" element={<LinkInBioPublic />} />

                                {/* Admin routes - protected by AdminRoute component */}
                                <Route path="/admin/*" element={
                                    <AdminRoute>
                                        <Admin />
                                    </AdminRoute>
                                } />

                                {/* Referral Dashboard */}
                                <Route path="/dashboard/affiliate" element={<ReferralDashboard />} />

                                {/* Callbacks moved to App.js for onboarding access */}


                                {/* Anything else not matched => go to dashboard */}
                                <Route path="*" element={<Navigate to="/dashboard" />} />
                            </Routes>
                        </Suspense>
                    )}
                </div>

                {isMobile && <HorizontalMobileNavbar />}
            </div>
        </SubscriptionGate>
    );
}

export default WorkspaceRoutes;
